import { axiom } from '../lib/axiom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Users, Clock, UserPlus, Mail, Check, Loader2, RefreshCw, Shield, Trash2, Activity, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

interface AllowedUser {
  email: string;
  access_level: string;
  invited_by: string | null;
  created_at: string;
  notes: string | null;
}

interface SignInLog {
  id: number;
  email: string;
  display_name: string | null;
  provider: string;
  action: string;
  ip_address: string;
  timestamp: string;
}

interface WaitlistEntry {
  id: number;
  email: string;
  verified: number;
  verified_at: string | null;
  created_at: string;
  hasAccess: boolean;
}

interface HealthData {
  status: 'healthy' | 'degraded' | 'unhealthy';
  checks: {
    d1: { status: 'ok' | 'error' | 'degraded'; latency_ms?: number; error?: string };
    r2: { status: 'ok' | 'error' | 'degraded'; latency_ms?: number; error?: string };
    kv: { status: 'ok' | 'error' | 'degraded'; latency_ms?: number; error?: string };
    dlq: { unresolved_count: number };
    axiom: { status: 'ok' | 'error' | 'skipped'; latency_ms?: number; error?: string };
  };
}

interface DlqEntry {
  id: string;
  workflow_id: string;
  step_name: string;
  error: string;
  context: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

interface DlqStats {
  unresolved: number;
  total: number;
  resolvedLast24h: number;
}

type TabId = 'health' | 'users' | 'waitlist' | 'dlq' | 'logs';

// Safe JSON parse that handles invalid JSON and already-parsed objects
function safeJsonParse(value: unknown): { parsed: unknown; isValid: boolean } {
  if (typeof value !== 'string') {
    // Already an object or other type
    return { parsed: value, isValid: true };
  }
  try {
    return { parsed: JSON.parse(value), isValid: true };
  } catch {
    return { parsed: value, isValid: false };
  }
}

export function Admin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('health');
  const [loading, setLoading] = useState(true);
  const [allowedUsers, setAllowedUsers] = useState<AllowedUser[]>([]);
  const [signInLogs, setSignInLogs] = useState<SignInLog[]>([]);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [dlqEntries, setDlqEntries] = useState<DlqEntry[]>([]);
  const [dlqStats, setDlqStats] = useState<DlqStats | null>(null);
  const [dlqError, setDlqError] = useState<string | null>(null);
  const [expandedDlq, setExpandedDlq] = useState<Set<string>>(new Set());
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const getAuthHeaders = useCallback(async () => {
    if (!user) return null;
    try {
      const token = await user.getIdToken();
      return {
        'Authorization': `Bearer ${token}`,
        'X-User-Email': user.email || '',
      };
    } catch (error) {
      axiom.error('Failed to get auth token', { error: error instanceof Error ? error.message : String(error) });
      return null;
    }
  }, [user]);

  const fetchHealth = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers) return;

    try {
      const res = await fetch(`${API_URL}/admin/health`, { headers });
      if (res.ok) {
        setHealth(await res.json());
      }
    } catch (error) {
      axiom.error('Error fetching health', { error: error instanceof Error ? error.message : String(error) });
    }
  }, [getAuthHeaders]);

  const fetchDlq = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers) return;

    setDlqError(null);
    try {
      const [entriesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/admin/dlq`, { headers }),
        fetch(`${API_URL}/admin/dlq/stats`, { headers }),
      ]);

      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setDlqEntries(data.items || []);
      } else {
        setDlqError(`Failed to fetch DLQ entries: ${entriesRes.status} ${entriesRes.statusText}`);
      }
      if (statsRes.ok) {
        setDlqStats(await statsRes.json());
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setDlqError(`Failed to fetch DLQ: ${message}`);
      axiom.error('Error fetching DLQ', { error: error instanceof Error ? error.message : String(error) });
    }
  }, [getAuthHeaders]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      // Fetch all data in parallel
      const [usersRes, waitlistRes] = await Promise.all([
        fetch(`${API_URL}/admin/users`, { headers }),
        fetch(`${API_URL}/admin/waitlist`, { headers }),
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setAllowedUsers(data.allowedUsers || []);
        setSignInLogs(data.recentSignIns || []);
      }

      if (waitlistRes.ok) {
        const data = await waitlistRes.json();
        setWaitlist(data.waitlist || []);
      }

      // Also fetch health and DLQ
      await Promise.all([fetchHealth(), fetchDlq()]);
    } catch (error) {
      axiom.error('Error fetching data', { error: error instanceof Error ? error.message : String(error) });
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, fetchHealth, fetchDlq]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh health every 30s when enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchHealth]);

  const approveUser = async (email: string) => {
    const headers = await getAuthHeaders();
    if (!headers) return;

    try {
      const res = await fetch(`${API_URL}/admin/users`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, notes: 'Approved from waitlist' }),
      });

      if (res.ok) {
        setMessage({ type: 'success', text: `Approved ${email}` });
        fetchData();
      } else {
        setMessage({ type: 'error', text: 'Failed to approve user' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Error approving user' });
    }
  };

  const deleteWaitlistEntry = async (email: string) => {
    if (!confirm(`Delete ${email} from waitlist?`)) return;
    const headers = await getAuthHeaders();
    if (!headers) return;

    try {
      const res = await fetch(`${API_URL}/admin/waitlist?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers,
      });

      if (res.ok) {
        setMessage({ type: 'success', text: `Deleted ${email} from waitlist` });
        fetchData();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete entry' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Error deleting entry' });
    }
  };

  const revokeAccess = async (email: string) => {
    if (!confirm(`Revoke access for ${email}?`)) return;
    const headers = await getAuthHeaders();
    if (!headers) return;

    try {
      const res = await fetch(`${API_URL}/admin/users?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers,
      });

      if (res.ok) {
        setMessage({ type: 'success', text: `Revoked access for ${email}` });
        fetchData();
      } else {
        setMessage({ type: 'error', text: 'Failed to revoke access' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Error revoking access' });
    }
  };

  const resolveDlqEntry = async (id: string) => {
    const headers = await getAuthHeaders();
    if (!headers) return;

    try {
      const res = await fetch(`${API_URL}/admin/dlq/${id}/resolve`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'DLQ entry resolved' });
        fetchDlq();
        fetchHealth();
      } else {
        setMessage({ type: 'error', text: 'Failed to resolve DLQ entry' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Error resolving DLQ entry' });
    }
  };

  const inviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    const headers = await getAuthHeaders();
    if (!headers) return;

    setInviting(true);
    try {
      const res = await fetch(`${API_URL}/admin/invite`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail, sendEmail: true }),
      });

      if (res.ok) {
        setMessage({ type: 'success', text: `Invited ${inviteEmail}` });
        setInviteEmail('');
        fetchData();
      } else {
        setMessage({ type: 'error', text: 'Failed to invite user' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Error inviting user' });
    } finally {
      setInviting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const toggleDlqExpand = (id: string) => {
    setExpandedDlq(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'ok':
        return 'text-green-400';
      case 'degraded':
        return 'text-yellow-400';
      case 'unhealthy':
      case 'error':
        return 'text-red-400';
      case 'skipped':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'ok':
        return 'bg-green-500/10';
      case 'degraded':
        return 'bg-yellow-500/10';
      case 'unhealthy':
      case 'error':
        return 'bg-red-500/10';
      case 'skipped':
        return 'bg-gray-500/10';
      default:
        return 'bg-gray-500/10';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#93c572]" />
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 hover:bg-[#1a1a1a] rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
          }`}>
            {message.text}
            <button onClick={() => setMessage(null)} className="ml-4 underline">Dismiss</button>
          </div>
        )}

        {/* Invite Form */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 mb-6 border border-[#2a2a2a]">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#93c572]" />
            Invite User
          </h2>
          <form onSubmit={inviteUser} className="flex gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="email@example.com"
              className="flex-1 px-4 py-2 bg-[#0a0a0a] border border-[#333] rounded-lg focus:outline-none focus:border-[#93c572]"
            />
            <button
              type="submit"
              disabled={inviting || !inviteEmail}
              className="px-6 py-2 bg-[#93c572] text-black font-medium rounded-lg hover:bg-[#7eb356] disabled:opacity-50 flex items-center gap-2"
            >
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Invite & Send Email
            </button>
          </form>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'health' as TabId, label: 'Health', icon: Activity, count: null, highlight: health?.status === 'unhealthy' || health?.status === 'degraded' },
            { id: 'users' as TabId, label: 'Users', icon: Users, count: allowedUsers.length },
            { id: 'waitlist' as TabId, label: 'Waitlist', icon: Clock, count: waitlist.filter(w => w.verified && !w.hasAccess).length },
            { id: 'dlq' as TabId, label: 'DLQ', icon: AlertTriangle, count: dlqStats?.unresolved || 0, highlight: (dlqStats?.unresolved || 0) > 0 },
            { id: 'logs' as TabId, label: 'Logs', icon: Clock, count: signInLogs.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#93c572] text-black'
                  : tab.highlight
                    ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                    : 'bg-[#1a1a1a] hover:bg-[#2a2a2a]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== null && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-black/20' : 'bg-[#333]'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#93c572]" />
          </div>
        ) : (
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] overflow-hidden">
            {/* Health Tab */}
            {activeTab === 'health' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">System Health</h3>
                  <label className="flex items-center gap-2 text-sm text-gray-400">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="rounded"
                    />
                    Auto-refresh (30s)
                  </label>
                </div>

                {health ? (
                  <div className="space-y-6">
                    {/* Overall Status */}
                    <div className={`p-6 rounded-xl ${getStatusBg(health.status)} border border-[#2a2a2a]`}>
                      <div className="flex items-center gap-3">
                        {health.status === 'healthy' ? (
                          <CheckCircle className="w-8 h-8 text-green-400" />
                        ) : health.status === 'degraded' ? (
                          <AlertTriangle className="w-8 h-8 text-yellow-400" />
                        ) : (
                          <XCircle className="w-8 h-8 text-red-400" />
                        )}
                        <div>
                          <div className={`text-2xl font-bold ${getStatusColor(health.status)}`}>
                            {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
                          </div>
                          <div className="text-gray-400 text-sm">Overall system status</div>
                        </div>
                      </div>
                    </div>

                    {/* Individual Checks */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* D1 Database */}
                      <div className="p-4 rounded-lg bg-[#0a0a0a] border border-[#2a2a2a]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">D1 Database</span>
                          <span className={`px-2 py-1 rounded text-xs ${getStatusBg(health.checks.d1.status)} ${getStatusColor(health.checks.d1.status)}`}>
                            {health.checks.d1.status}
                          </span>
                        </div>
                        {health.checks.d1.latency_ms !== undefined && (
                          <div className="text-2xl font-mono">
                            {health.checks.d1.latency_ms}ms
                            <span className="text-sm text-gray-500 ml-2">latency</span>
                          </div>
                        )}
                        {health.checks.d1.error && (
                          <div className="text-red-400 text-sm mt-2">{health.checks.d1.error}</div>
                        )}
                      </div>

                      {/* R2 Storage */}
                      <div className="p-4 rounded-lg bg-[#0a0a0a] border border-[#2a2a2a]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">R2 Storage</span>
                          <span className={`px-2 py-1 rounded text-xs ${getStatusBg(health.checks.r2.status)} ${getStatusColor(health.checks.r2.status)}`}>
                            {health.checks.r2.status}
                          </span>
                        </div>
                        {health.checks.r2.latency_ms !== undefined && (
                          <div className="text-2xl font-mono">
                            {health.checks.r2.latency_ms}ms
                            <span className="text-sm text-gray-500 ml-2">latency</span>
                          </div>
                        )}
                        {health.checks.r2.error && (
                          <div className="text-red-400 text-sm mt-2">{health.checks.r2.error}</div>
                        )}
                      </div>

                      {/* KV Store */}
                      <div className="p-4 rounded-lg bg-[#0a0a0a] border border-[#2a2a2a]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">KV Store</span>
                          <span className={`px-2 py-1 rounded text-xs ${getStatusBg(health.checks.kv.status)} ${getStatusColor(health.checks.kv.status)}`}>
                            {health.checks.kv.status}
                          </span>
                        </div>
                        {health.checks.kv.latency_ms !== undefined && (
                          <div className="text-2xl font-mono">
                            {health.checks.kv.latency_ms}ms
                            <span className="text-sm text-gray-500 ml-2">latency</span>
                          </div>
                        )}
                        {health.checks.kv.error && (
                          <div className="text-red-400 text-sm mt-2">{health.checks.kv.error}</div>
                        )}
                      </div>

                      {/* Axiom Observability */}
                      <div className="p-4 rounded-lg bg-[#0a0a0a] border border-[#2a2a2a]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">Axiom</span>
                          <span className={`px-2 py-1 rounded text-xs ${getStatusBg(health.checks.axiom.status)} ${getStatusColor(health.checks.axiom.status)}`}>
                            {health.checks.axiom.status}
                          </span>
                        </div>
                        {health.checks.axiom.latency_ms !== undefined && (
                          <div className="text-2xl font-mono">
                            {health.checks.axiom.latency_ms}ms
                            <span className="text-sm text-gray-500 ml-2">latency</span>
                          </div>
                        )}
                        {health.checks.axiom.status === 'skipped' ? (
                          <div className="text-gray-500 text-sm mt-2">Token not configured</div>
                        ) : health.checks.axiom.error ? (
                          <div className="text-red-400 text-sm mt-2">{health.checks.axiom.error}</div>
                        ) : null}
                      </div>

                      {/* DLQ */}
                      <div className="p-4 rounded-lg bg-[#0a0a0a] border border-[#2a2a2a]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">Dead Letter Queue</span>
                          {health.checks.dlq.unresolved_count > 0 ? (
                            <span className="px-2 py-1 rounded text-xs bg-yellow-500/10 text-yellow-400">
                              needs attention
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs bg-green-500/10 text-green-400">
                              clear
                            </span>
                          )}
                        </div>
                        <div className="text-2xl font-mono">
                          {health.checks.dlq.unresolved_count}
                          <span className="text-sm text-gray-500 ml-2">unresolved</span>
                        </div>
                        {health.checks.dlq.unresolved_count > 0 && (
                          <button
                            onClick={() => setActiveTab('dlq')}
                            className="text-sm text-[#93c572] hover:underline mt-2"
                          >
                            View DLQ entries
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    Failed to load health data
                  </div>
                )}
              </div>
            )}

            {/* DLQ Tab */}
            {activeTab === 'dlq' && (
              <div>
                {/* Stats Summary */}
                {dlqStats && (
                  <div className="p-4 bg-[#0a0a0a] border-b border-[#2a2a2a] flex gap-6">
                    <div>
                      <span className="text-gray-400 text-sm">Unresolved</span>
                      <div className={`text-xl font-bold ${dlqStats.unresolved > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                        {dlqStats.unresolved}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Resolved (24h)</span>
                      <div className="text-xl font-bold text-gray-300">{dlqStats.resolvedLast24h}</div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Total</span>
                      <div className="text-xl font-bold text-gray-300">{dlqStats.total}</div>
                    </div>
                  </div>
                )}

                {/* DLQ Entries */}
                {dlqEntries.length > 0 ? (
                  <div className="divide-y divide-[#2a2a2a]">
                    {dlqEntries.map((entry) => (
                      <div key={entry.id} className="hover:bg-[#2a2a2a]/50">
                        <div
                          className="px-4 py-3 flex items-center gap-3 cursor-pointer"
                          onClick={() => toggleDlqExpand(entry.id)}
                        >
                          {expandedDlq.has(entry.id) ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-gray-400">
                                {entry.workflow_id.slice(0, 8)}...
                              </span>
                              <span className="px-2 py-0.5 rounded text-xs bg-[#333]">
                                {entry.step_name}
                              </span>
                            </div>
                            <div className="text-sm text-red-400 truncate mt-1">
                              {entry.error}
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(entry.created_at)}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              resolveDlqEntry(entry.id);
                            }}
                            className="px-3 py-1 bg-[#93c572] text-black text-sm rounded hover:bg-[#7eb356]"
                          >
                            Resolve
                          </button>
                        </div>

                        {/* Expanded Details */}
                        {expandedDlq.has(entry.id) && (
                          <div className="px-4 pb-4 pl-11">
                            <div className="bg-[#0a0a0a] rounded-lg p-4 space-y-3">
                              <div>
                                <div className="text-xs text-gray-500 mb-1">Workflow ID</div>
                                <div className="font-mono text-sm">{entry.workflow_id}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 mb-1">Error</div>
                                <div className="text-sm text-red-400 whitespace-pre-wrap">{entry.error}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 mb-1">Context</div>
                                <pre className="text-xs bg-[#1a1a1a] p-2 rounded overflow-x-auto">
                                  {(() => {
                                    const { parsed, isValid } = safeJsonParse(entry.context);
                                    if (!isValid) return String(parsed || '(empty)');
                                    return JSON.stringify(parsed, null, 2);
                                  })()}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : dlqError ? (
                  <div className="px-4 py-8 text-center">
                    <div className="text-red-400 mb-2">Failed to load DLQ data</div>
                    <div className="text-sm text-gray-500">{dlqError}</div>
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center text-gray-500">
                    No unresolved DLQ entries
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <table className="w-full">
                <thead className="bg-[#0a0a0a]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Level</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Invited By</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Date</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2a2a2a]">
                  {allowedUsers.map((u) => (
                    <tr key={u.email} className="hover:bg-[#2a2a2a]/50">
                      <td className="px-4 py-3">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          u.access_level === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-[#93c572]/20 text-[#93c572]'
                        }`}>
                          {u.access_level}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400">{u.invited_by || '-'}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{formatDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => revokeAccess(u.email)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                  {allowedUsers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        No approved users yet. Invite someone above!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Waitlist Tab */}
            {activeTab === 'waitlist' && (
              <table className="w-full">
                <thead className="bg-[#0a0a0a]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Signed Up</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2a2a2a]">
                  {waitlist.map((w) => (
                    <tr key={w.id} className="hover:bg-[#2a2a2a]/50">
                      <td className="px-4 py-3">{w.email}</td>
                      <td className="px-4 py-3">
                        {w.hasAccess ? (
                          <span className="flex items-center gap-1 text-[#93c572]">
                            <Check className="w-4 h-4" /> Has Access
                          </span>
                        ) : w.verified ? (
                          <span className="text-yellow-400">Verified, Pending</span>
                        ) : (
                          <span className="text-gray-500">Unverified</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{formatDate(w.created_at)}</td>
                      <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                        {!w.hasAccess && w.verified && (
                          <button
                            onClick={() => approveUser(w.email)}
                            className="px-3 py-1 bg-[#93c572] text-black text-sm rounded hover:bg-[#7eb356]"
                          >
                            Approve
                          </button>
                        )}
                        <button
                          onClick={() => deleteWaitlistEntry(w.email)}
                          className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                          title="Delete from waitlist"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {waitlist.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                        No waitlist entries yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
              <table className="w-full">
                <thead className="bg-[#0a0a0a]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Time</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Provider</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2a2a2a]">
                  {signInLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-[#2a2a2a]/50">
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(log.timestamp)}</td>
                      <td className="px-4 py-3">{log.email}</td>
                      <td className="px-4 py-3 text-gray-400">{log.display_name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded text-xs bg-[#2a2a2a]">
                          {log.provider === 'google.com' ? 'Google' : 'Email Link'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm font-mono">{log.ip_address}</td>
                    </tr>
                  ))}
                  {signInLogs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        No sign-in logs yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
